<template>
  <v-container
    fluid
    class="mx-auto"
    ref="eventViewContainer"
    :style="$vuetify.breakpoint.mdAndUp ? 'width: 75%' : 'width: 100%'"
  >
    <v-card v-if="event.no !== decryptedRoute.params.no" flat>
      <v-row>
        <v-col cols="12" md="6">
          <v-skeleton-loader
            type="card, list-item-three-line, card-heading, list-item-three-line"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-skeleton-loader
            type="list-item-three-line, list-item-avatar, card-heading"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="2" v-for="col in 5" :key="col">
          <v-skeleton-loader type="avatar" />
        </v-col>
        <v-col cols="12">
          <v-skeleton-loader type="image" />
        </v-col>
      </v-row>
    </v-card>

    <v-card v-if="event.no === decryptedRoute.params.no" flat class="mt-2">
      <template v-if="event.disclaimer.length">
        <div
          v-for="disclaimer in event.disclaimer"
          :key="disclaimer.documentNo"
        >
          <v-alert type="warning" class="mb-3">{{
            disclaimer.description
          }}</v-alert>
        </div>
      </template>

      <v-row>
        <v-col cols="12" md="7">
          <v-card flat>
            <v-img
              :src="url + event.base64Logo"
              :lazy-src="require(`@/assets/images/picture4.jpg`)"
              :height="$vuetify.breakpoint.mdAndUp ? '454px' : '224px'"
              :width="$vuetify.breakpoint.mdAndUp ? '800px' : '320px'"
              style="border-radius: 5px"
              class="mx-auto"
            />

            <v-card-title> Event Information </v-card-title>

            <v-card-text v-html="event.excerpt" />

            <v-card-title> Event Attachments </v-card-title>

            <v-card-text>
              <v-row>
                <v-col
                  cols="4"
                  v-for="(attachment, i) in event.eventsDocuments"
                  :key="i"
                >
                  <v-sheet
                    :color="fileMeta(attachment.documentFormat).color"
                    dark
                    outlined
                    rounded
                  >
                    <v-list-item link @click="viewDoc(attachment)">
                      <v-list-item-icon>
                        <v-icon>
                          {{ fileMeta(attachment.documentFormat).icon }}
                        </v-icon>
                      </v-list-item-icon>

                      <v-list-item-content>
                        <v-list-item-title>
                          {{ attachment.documentDescription }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ attachment.description }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-sheet>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-text>
              <EventCountdown :start-date="getStartTime" />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="5">
          <v-card flat class="mx-auto">
            <v-row no-gutters>
              <v-col cols="12" md="10">
                <!-- Description -->
                <div
                  class="text-capitalize text-wrap text-lg-h4 font-weight-bold"
                >
                  {{ event.description }}
                </div>
                <!-- Date and time -->
                <div>
                  <v-icon small>mdi-calendar-multiselect</v-icon>
                  <span> Upcoming: {{ formatDate(event.startDate, 6) }} </span>
                  <br />

                  <div v-if="event.location !== 'Online'">
                    <a
                      class="text-capitalize mt-2 text-body-1 font-weight-bold text-justify text-decoration-none"
                      @click="
                        $vuetify.goTo('#map', {
                          duration: 500,
                          easing: 'easeInOutCubic',
                        })
                      "
                    >
                      <v-icon color="primary">mdi-map-marker</v-icon>
                      {{ event.location }}
                    </a>
                  </div>

                  <span v-else class="font-weight-bold">{{
                    event.location
                  }}</span>
                </div>
              </v-col>
              <v-col cols="12" md="2">
                <div style="width: 100%">
                  <badge v-if="event.cPDTerms" :cpd-caption="event.cPDTerms" />
                  <CPDbadge v-else :cpd-points="event.cpdHours" />
                </div>
              </v-col>
            </v-row>
            <!-- slots -->
            <v-list dense class="mt-n2">
              <v-list-item dense class="pa-0">
                <v-list-item-content
                  class="stats"
                  v-if="parseInt(event.remainingSlots) !== 0"
                >
                  <v-list-item-subtitle>
                    <v-avatar
                      class="secondary lighten-5 text--darken-4 text-center mr-2"
                    >
                      <span
                        class="secondary--text text--darken-4 text-center text-body-1 font-weight-bold px-1"
                        >{{ event.remainingSlots }}</span
                      >
                    </v-avatar>
                    <span class="text-body-2 font-weight-bold"
                      >Slots Available</span
                    >
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-content class="stats" v-else>
                  <div class="textStyle">Slots Available</div>

                  <v-list-item-subtitle class="textStyle2">
                    {{ event.slots }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <!-- Buttons -->
            <v-row dense>
              <v-col cols="6" md6 sm6>
                <v-btn
                  depressed
                  @click="
                    $root.routeTo({
                      name: 'booking',
                      params: { no: event.no },
                    })
                  "
                  color="primary"
                  class="toggleButtons mx-2"
                  block
                >
                  <v-icon left>book_online</v-icon>
                  Register here
                </v-btn>
              </v-col>
              <!--Button for site Visit-->
              <v-col cols="6" md6 sm6>
                <v-btn
                  depressed
                  v-if="event.hasRelatedEvent"
                  @click="showRelatedEventDialog()"
                  color="primary"
                  class="toggleButtons mx-2"
                  block
                >
                  <v-icon left>event</v-icon>
                  {{ settings.SiteVisit }}
                </v-btn>
              </v-col>
            </v-row>

            <!--Event costs-->
            <EventPricingView
              :filtered-packages="filteredPackages"
              :check-good-standing="event.checkGoodStanding"
            />
            <!--Packages-->
            <v-card-title v-if="filteredPackages.otherpackages.length !== 0"
              >Packages</v-card-title
            >

            <v-card-text>
              <v-list dense v-if="filteredPackages.otherpackages.length !== 0">
                <div
                  v-for="(cost, i) in filteredPackages.otherpackages
                    .slice()
                    .reverse()"
                  :key="i"
                >
                  <v-list-item
                    dense
                    ripple
                    :two-line="cost.amountIncludingVAT !== 0"
                  >
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ cost.description }}
                      </v-list-item-title>

                      <v-list-item-subtitle
                        class="green--text overline text-wrap"
                        v-if="cost.amountIncludingVAT !== 0"
                      >
                        KES{{ cost.amountInclLCY | currency }}
                      </v-list-item-subtitle>

                      <v-list-item-title
                        v-if="
                          cost.amountIncludingVAT !== 0 &&
                          cost.currencyCode !== 'KES'
                        "
                      >
                        Foreign Delegate
                      </v-list-item-title>

                      <v-list-item-subtitle
                        class="green--text overline text-wrap"
                        v-if="
                          cost.amountIncludingVAT !== 0 &&
                          cost.currencyCode !== 'KES'
                        "
                      >
                        {{ cost.currencyCode }}
                        {{ cost.amountIncludingVAT | currency }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </div>
              </v-list>
            </v-card-text>

            <v-card-title> Contact information </v-card-title>

            <v-card-text>
              <h4>Contact Person</h4>
              <p>{{ event.contactPersonName }}</p>

              <h4>Phone</h4>
              <p>{{ event.contactPersonPhoneNo }}</p>

              <h4>Email</h4>
              <p>{{ event.contactPersonEmail }}</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row no-gutters class="mb-16">
        <v-col cols="12">
          <div class="d-flex justify-start">
            <v-img
              :height="$vuetify.breakpoint.mobile ? '100%' : '100'"
              v-for="(sponsor, i) in event.eventSponsor"
              :key="i"
              contain
              width="10%"
              class="align-self-start"
              :src="url + sponsor.base64Picture"
            />
          </div>
        </v-col>
        <v-col cols="12" style="position: relative; z-index: 1">
          <v-card class="my-4" id="map">
            <div style="height: 400px; width: 100%">
              <l-map
                :zoom="leafMap.zoom"
                :center="leafMap.center"
                :options="leafMap.mapOptions"
                style="height: 100%"
                @update:center="centerUpdate"
                @update:zoom="zoomUpdate"
              >
                <l-tile-layer
                  :url="leafMap.url"
                  :attribution="leafMap.attribution"
                />

                <l-marker :lat-lng="leafMap.withPopup">
                  <l-popup>
                    <div>I am a popup</div>
                  </l-popup>
                </l-marker>
              </l-map>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <PreviewDialog
      :dialog="dialog"
      @close-dialog="closeDialog"
      :document="document"
    />
    <ChildEventsDialog
      :dialog="relatedEventDialog"
      :events="relatedEvent"
      @close-dialog="closeRelatedEventDialog"
    />
  </v-container>
</template>

<script>
import { helper } from "../../../utilities";
import eventMixin from "../eventMixin";
import { latLng, Icon } from "leaflet";
import { LMap, LTileLayer, LMarker, LPopup } from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import PreviewDialog from "./PreviewDialog.vue";
import EventPricingView from "./EventPricingView.vue";
import CPDbadge from "../views/CPDbadge.vue";
import badge from "../views/badge.vue";
import EventCountdown from "./EventCountdown.vue";
import { company } from "@/environment/environment";
import ChildEventsDialog from "./ChildEventsDialog.vue";

// Fix for icon Markers
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "EventView",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    PreviewDialog,
    EventPricingView,
    CPDbadge,
    badge,
    EventCountdown,
    ChildEventsDialog,
  },
  mixins: [eventMixin],
  data: function () {
    return {
      isPhysicalForIEK: true,
      firstButtonIndex: 0,
      iekSpecificEvent: ["Physical Attendance", "Virtual Attendance"],
      delegateCategory: "Member",
      map: {
        lat: "",
        long: "",
      },
      dialog: false,
      relatedEventDialog: false,
      document: null,
      orgName: company,
    };
  },

  beforeRouteEnter(to, from, next) {
    next((v) => {
      v.$store.dispatch(
        "Events/getEventMeta",
        v.$root.decryptRoute(to).params.no
      );
      v.$store.dispatch("Events/getEvent", v.$root.decryptRoute(to).params.no);
      v.$store.dispatch("Events/getMembershipCategories");
    });
  },

  mounted() {
    // scroll to top of page
    window.scrollTo(0, 0);
  },

  computed: {
    getStartTime() {
      // combine startDate and startTime
      return this.event
        ? new Date(this.event.startDate + " " + this.event.startTime)
        : new Date();
    },

    event() {
      return this.$store.getters["Events/event"];
    },
    //fn ()get related event
    relatedEvent() {
      if (this.event) {
        if (this.event.relatedEvent && this.event.relatedEvent.length > 0) {
          return this.event.relatedEvent;
        }
      }
      return [];
    },
    leafMap() {
      //-1.2832533,36.8172449

      let latLong = latLng(
        parseFloat(this.event.gPSLatitude),
        parseFloat(this.event.gPSLongitude)
      );

      return {
        zoom: 13,
        center: latLong,
        url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        attribution:
          '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        withPopup: latLong,
        withTooltip: latLong,
        currentZoom: 11.5,
        currentCenter: latLong,
        showParagraph: false,
        mapOptions: {
          zoomSnap: 0.5,
        },
        showMap: true,
      };
    },

    filteredPackages() {
      let eventpackages = {
        eventCosts: [],
        otherpackages: [],
      };

      if (this.event)
        this.event.eventPackageCost
          ? this.event.eventPackageCost.forEach((cost) => {
              if ((cost.defaultPackage || cost.mandatory) && !cost.hideCost) {
                eventpackages.eventCosts.push(cost);
              }

              if (!cost.mandatory && !cost.hideCost)
                eventpackages.otherpackages.push(cost);
            })
          : [];

      return eventpackages;
    },

    decryptedRoute() {
      return this.$root.decryptRoute(this.$route);
    },

    settings() {
      return this.$store.getters["Admin/adminGetters"]("settings");
    },
  },

  methods: {
    setIEKPhysical(id) {
      this.firstButtonIndex = id;
      if (this.isPhysicalForIEK && id !== 0) {
        this.isPhysicalForIEK = !this.isPhysicalForIEK;
        return;
      }
      if (!this.isPhysicalForIEK && id === 0) {
        this.isPhysicalForIEK = !this.isPhysicalForIEK;
        return;
      }
    },
    fileMeta: function (file) {
      return {
        icon: helper.getFileIcon(file),
        color: helper.getFileIconColor(file),
      };
    },

    isEarlyBird: function (eventPackage) {
      return new Date(eventPackage.earlyBirdCutoffDate) > new Date();
    },

    zoomUpdate(zoom) {
      this.currentZoom = zoom;
    },

    centerUpdate(center) {
      this.currentCenter = center;
    },

    images(number) {
      return require(`@/assets/images/picture${number}.jpg`);
    },

    closeDialog: function (val) {
      this.dialog = val;
    },

    //fn ()-> add book for site-visit
    showRelatedEventDialog() {
      this.relatedEventDialog = true;
    },
    closeRelatedEventDialog() {
      this.relatedEventDialog = false;
    },
  },
  //fn -> watch for relatedEvent & dispatch action
  watch: {
    relatedEvent(newVal) {
      if (newVal) {
        const eve = newVal.map((item) => {
          return {
            no: item.childEventNo,
          };
        });
        this.$store.dispatch("Events/getRelatedEvent", eve);
      }
    },
  },
};
</script>
<style scoped>
@import "../EventView.css";

@media screen and (max-width: 320px) {
  /* Container adjustments */
  .v-container {
    padding: 8px !important;
  }

  .v-card .v-image {
    width: 100% !important;
    height: 180px !important;
    border-radius: 4px !important;
  }
  .text-lg-h4 {
    font-size: 1.1rem !important;
    line-height: 1.3 !important;
  }

  .v-card__title {
    font-size: 1rem !important;
    padding: 12px 8px !important;
  }

  .v-card__text {
    padding: 8px !important;
    font-size: 0.85rem !important;
  }

  .v-col.md-2 > div {
    width: 100% !important;
    margin-top: 8px;
  }

  .v-card__text .v-row .v-col[class*="cols-4"] {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    margin-bottom: 8px;
  }

  .v-list-item__title {
    font-size: 0.85rem !important;
    white-space: normal !important;
  }

  .v-list-item__subtitle {
    font-size: 0.75rem !important;
  }

  .v-row.dense .v-col {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    padding: 4px 0 !important;
  }

  .v-btn.toggleButtons {
    width: 100% !important;
    margin: 4px 0 !important;
    font-size: 0.8rem !important;
    padding: 0 12px !important;
    height: 40px !important;
  }

  .v-btn {
    font-size: 0.75rem !important;
    padding: 0 8px !important;
    height: 36px !important;
  }

  .v-btn .v-icon {
    font-size: 18px !important;
  }
  .v-chip {
    font-size: 0.7rem !important;
    height: 24px !important;
  }

  .stats .v-avatar {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
  }

  .stats .text-body-1 {
    font-size: 0.8rem !important;
  }

  .stats .text-body-2 {
    font-size: 0.75rem !important;
  }

  section.eventCostSection {
    width: 100% !important;
    padding: 0 !important;
    box-sizing: border-box !important;
  }

  section.eventCostSection .v-btn {
    width: 100% !important;
    margin: 8px 0 !important;
    float: none !important;
  }

  div.section-filters {
    flex-direction: column !important;
    margin: 8px 0;
  }

  div.section-filters * {
    margin: 0 0 4px 0 !important;
  }

  .section-filters h4,
  .section-filters .activeAttendance {
    font-size: 0.9rem !important;
    margin-bottom: 8px;
  }

  .table {
    width: 100% !important;
    padding: 2% 0 !important;
    font-size: 0.7rem !important;
    table-layout: fixed !important;
    border-collapse: collapse !important;
  }

  .table th,
  .table td {
    padding: 6px 4px !important;
    font-size: 0.65rem !important;
    word-wrap: break-word !important;
    white-space: normal !important;
    vertical-align: top !important;
    border: 1px solid #e0e0e0 !important;
  }

  .table th {
    font-size: 0.6rem !important;
    line-height: 1.2 !important;
  }

  .table th p {
    margin: 0 0 2px 0 !important;
    font-size: 0.6rem !important;
    font-weight: bold !important;
  }
  .table td:first-child,
  .table th:first-child {
    width: 45% !important;
  }

  .table td:nth-child(2),
  .table td:nth-child(3),
  .table th:nth-child(2),
  .table th:nth-child(3) {
    width: 27.5% !important;
    text-align: center !important;
  }
  .v-card__text h4 {
    font-size: 0.9rem !important;
    margin-bottom: 2px;
  }

  .v-card__text p {
    font-size: 0.8rem !important;
    margin-bottom: 8px;
  }
  #map {
    height: 250px !important;
  }

  #map > div {
    height: 250px !important;
  }
  .v-img.align-self-start {
    width: 20% !important;
    min-width: 50px;
  }
  .v-alert {
    font-size: 0.8rem !important;
    padding: 8px !important;
  }
  .text-body-1 {
    font-size: 0.8rem !important;
  }

  .overline {
    font-size: 0.65rem !important;
  }
  .d-flex.flex-row {
    flex-direction: column !important;
  }

  .d-flex.flex-row > div[style*="width: 80px"] {
    width: auto !important;
    margin-bottom: 4px;
  }
  .v-skeleton-loader {
    max-width: 100% !important;
  }
}

@media screen and (min-width: 321px) and (max-width: 480px) {
  .v-container {
    padding: 12px !important;
  }

  .text-lg-h4 {
    font-size: 1.25rem !important;
    line-height: 1.4 !important;
  }

  .v-card__title {
    font-size: 1.1rem !important;
    padding: 14px 12px !important;
  }

  .v-card__text {
    padding: 12px !important;
    font-size: 0.9rem !important;
  }

  .v-card__text .v-row .v-col[class*="cols-4"] {
    flex: 0 0 50% !important;
    max-width: 50% !important;
  }

  .v-row.dense .v-col {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    padding: 4px 8px !important;
  }

  .v-btn.toggleButtons {
    width: 100% !important;
    margin: 4px 0 !important;
  }

  .v-btn {
    font-size: 0.8rem !important;
  }

  .toggleButtons {
    font-size: 0.75rem !important;
  }
  section.eventCostSection {
    width: 100% !important;
    padding: 4px !important;
  }

  section.eventCostSection .v-btn {
    width: 100% !important;
    float: none !important;
  }

  .table {
    width: 100% !important;
    font-size: 0.75rem !important;
  }

  .table th,
  .table td {
    padding: 6px !important;
    font-size: 0.7rem !important;
  }

  #map,
  #map > div {
    height: 300px !important;
  }
}

@media screen and (max-width: 480px) {
  section.eventCostSection {
    -webkit-overflow-scrolling: touch;
  }
}
</style>
